/* ============================================================================
   customize-product-redesign.js
   Isolated, additive behaviour for the Customize / Replacement product PDP.

   HARD RULES (see project guardrails):
   - Reads existing configurator state selectors ONLY; never writes to any
     price / variant / cart node, never mutates the decoy price data-attributes,
     never calls the submit flow. All add-to-cart still runs through the
     existing .itg_submit_btn handler untouched.
   - Owns four purely-presentational things:
       (a) adds the `pdp-v2` class to <body>,
       (b) drives the step indicator (#2/#4) from USER INTERACTION (not from the
           default-selected variants) so on load only Frame is current,
       (c) pins the announcement bar with the header (#1),
       (d) the mobile mini product thumbnail (#1-media): relocate, refresh, jump,
           auto-hide when the real gallery is visible.
   - The sticky checkout bar (#2/#6) keeps its own self-contained script in
     snippets/mobile-sticky-atc.liquid.
   - Everything is guarded; a failure in any part must not break the page.
   ========================================================================== */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(function () {
    var root = document.querySelector('.customize_main_product_wrapper');
    var config = document.querySelector('.cstm_customize__product');
    // Only run on the configurator PDP.
    if (!root || !config) return;

    document.body.classList.add('pdp-v2');
    config.classList.add('pdp-v2');

    initStickyHeader();       // #1 announcement bar + header
    initSteps(root);          // #2 / #4 step indicator + step-card checkmarks
    initMiniView(root);       // #1 mobile mini thumbnail
    initGalleryRail(root);    // #2 desktop left thumbnail rail height = main image
  });

  /* --------------------------------------------------------------------------
     #1 · STICKY ANNOUNCEMENT BAR + HEADER
     Both header-group sections share the same auto-class, so pinning them with
     one rule made them overlap at top:0. Here we pin the announcement section
     at top:0 and offset the header section by the announcement's height, and
     neutralise Dawn's on-scroll-up hide so both stay visible.
     -------------------------------------------------------------------------- */
  function initStickyHeader() {
    try {
      var annc = document.querySelector('.utility-bar');
      var headerWrap = document.querySelector('.header-wrapper');
      var anncSection = annc ? annc.closest('.shopify-section') : null;
      var headerSection = headerWrap ? headerWrap.closest('.shopify-section') : null;
      if (!anncSection || !headerSection) return;

      // Two possible DOM shapes:
      //  (a) both sections wrapped in a single header-group container → pin the
      //      container (they move together, no overlap, no offset math);
      //  (b) both sections are siblings (e.g. direct children of <body>) → pin
      //      the announcement at top:0 and offset the header by its height.
      var parent = anncSection.parentElement;
      var grouped = parent && parent === headerSection.parentElement &&
                    parent !== document.body && parent.tagName !== 'MAIN';

      if (grouped) {
        parent.classList.add('pdp-headgroup-sticky');
      } else {
        anncSection.classList.add('pdp-annc-sticky');
        headerSection.classList.add('pdp-head-sticky');
      }

      // Publish the offsets used by scoped CSS:
      //  --pdp-annc-h      : announcement height (header sits below it)
      //  --pdp-headstack-h : full sticky header stack height (announcement +
      //                      header) — the mobile sticky step bar sits below it.
      var setOffset = function () {
        var annH = anncSection.offsetHeight;
        var stackH = grouped ? parent.offsetHeight : (annH + headerSection.offsetHeight);
        var rootStyle = document.documentElement.style;
        rootStyle.setProperty('--pdp-annc-h', annH + 'px');
        rootStyle.setProperty('--pdp-headstack-h', stackH + 'px');
      };
      setOffset();
      window.addEventListener('resize', setOffset);
      // Re-measure once fonts/rotating announcements settle.
      window.addEventListener('load', setOffset);
    } catch (e) { /* no-op */ }
  }

  /* --------------------------------------------------------------------------
     #2 / #4 · STEP INDICATOR (scroll-position driven)
     The progress bar tracks SCROLL POSITION, not variant selection, in BOTH
     directions: on every update it finds the single section in the active zone
     (~35% down the viewport) and sets ONLY that step "current" — earlier steps
     show as done, later steps are cleared. Scrolling up de-activates later steps
     (Vision→Lens→Frame) just as scrolling down activates them. Frame is current
     on load.

     This ONLY changes what triggers the active/complete state — variant SELECTION
     is unchanged (handled in customize-main-product.js) and the visual classes
     (.is-current / .is-done) are exactly the same as before.

     Mechanism: an IntersectionObserver on the three section cards fires as they
     cross the active zone; an rAF-throttled scroll/resize fallback keeps it in
     sync during fast scrolling / momentum on both desktop and mobile.
     -------------------------------------------------------------------------- */
  function initSteps(root) {
    var steps = root.querySelector('.pdp-steps');
    if (!steps) return;

    var stepEls = steps.querySelectorAll('.pdp-step');

    // The three step SECTION containers (also the click-to-scroll targets).
    // Order matches the stepper: Frame, Lens, Vision.
    var sections = [
      root.querySelector('.pdp-stepcard--frame'),
      root.querySelector('.pdp-stepcard--lens'),
      root.querySelector('.pdp-stepcard--vision')
    ];

    // The section currently in the active zone = the last one whose top has
    // crossed the trigger line (~35% down the viewport). Defaults to Frame (0),
    // so Frame is current at the top of the page; at the bottom the last section
    // whose top has passed the line wins (Vision). Direction-agnostic — it just
    // reads live geometry, so scrolling up returns an earlier index naturally.
    function computeCurrent() {
      var line = window.innerHeight * 0.35;
      var idx = 0;
      for (var i = 0; i < sections.length; i++) {
        if (sections[i] && sections[i].getBoundingClientRect().top <= line) idx = i;
      }
      return idx;
    }

    function render(current) {
      // Exactly ONE step is current (the in-view section); earlier steps show as
      // done, later steps are inactive. Derived from `current` every tick (no
      // monotonic latch), so the state clears correctly when scrolling back up.
      for (var i = 0; i < stepEls.length; i++) {
        stepEls[i].classList.toggle('is-current', i === current);
        stepEls[i].classList.toggle('is-done', i < current);
      }
      // Mirror the same state onto the section cards (number → checkmark).
      for (var j = 0; j < sections.length; j++) {
        if (sections[j]) sections[j].classList.toggle('is-done', j < current);
      }
    }

    var ticking = false;
    function update() {
      ticking = false;
      render(computeCurrent());
    }
    function requestUpdate() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    // Primary trigger: IntersectionObserver on each section card. The thin
    // rootMargin band mirrors the ~35% trigger line so a crossing fires a
    // recompute (which reads live geometry, so it's exact regardless of timing).
    if (typeof window.IntersectionObserver !== 'undefined') {
      try {
        var io = new IntersectionObserver(function () { requestUpdate(); }, {
          root: null,
          rootMargin: '-35% 0px -60% 0px',
          threshold: [0, 1]
        });
        sections.forEach(function (s) { if (s) io.observe(s); });
      } catch (e) { /* no-op */ }
    }
    // Fallback for fast scrolling / momentum where the observer can batch late.
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    window.addEventListener('load', requestUpdate);

    // Clicking a step still smooth-scrolls to its section (navigation aid only).
    stepEls.forEach(function (el, i) {
      el.addEventListener('click', function () {
        if (sections[i]) sections[i].scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });

    update(); // Frame current on load
  }

  /* --------------------------------------------------------------------------
     #1-media · MOBILE MINI PRODUCT THUMBNAIL
     -------------------------------------------------------------------------- */
  function initMiniView(root) {
    var mini = document.getElementById('PdpMiniView');
    var mediaWrap = root.querySelector('.product__media-wrapper');
    if (!mini || !mediaWrap) return;

    var img = mini.querySelector('img');

    if (mini.parentNode !== document.body) document.body.appendChild(mini);

    function currentMainSrc() {
      var el =
        root.querySelector('.no_frameimage_list .product__media-item.is-active img') ||
        root.querySelector('.product__media-item.is-active img') ||
        mediaWrap.querySelector('img');
      return el ? (el.currentSrc || el.src) : '';
    }

    function refresh() {
      var src = currentMainSrc();
      if (img && src && img.src !== src) img.src = src;
    }
    refresh();

    try {
      var mo = new MutationObserver(refresh);
      mo.observe(mediaWrap, { subtree: true, attributes: true, attributeFilter: ['class', 'src'] });
    } catch (e) { /* no-op */ }

    mini.addEventListener('click', function () {
      mediaWrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    if (typeof window.IntersectionObserver !== 'undefined') {
      try {
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              mini.classList.toggle('is-hidden', entry.isIntersecting);
            });
          },
          { root: null, threshold: 0.15 }
        );
        io.observe(mediaWrap);
      } catch (e) { /* no-op */ }

      // Mobile: once the sticky step bar scrolls past the Product Information
      // section (no longer pinned/visible), shift the thumbnail up into the
      // space it vacated, matching the mockup.
      var steps = root.querySelector('.pdp-steps');
      if (steps) {
        try {
          var io2 = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                mini.classList.toggle('pdp-miniview--up', !entry.isIntersecting);
              });
            },
            { root: null, threshold: 0 }
          );
          io2.observe(steps);
        } catch (e) { /* no-op */ }
      }
    }
  }

  /* --------------------------------------------------------------------------
     #2 · DESKTOP LEFT THUMBNAIL RAIL HEIGHT
     Cap the vertical thumbnail rail to the main image's height and let it
     scroll internally (CSS handles overflow), so the gallery never grows taller
     than the image. Publishes the measured height as --pdp-rail-h. Desktop only.
     -------------------------------------------------------------------------- */
  function initGalleryRail(root) {
    try {
      var gallery = root.querySelector('media-gallery');
      if (!gallery) return;
      var viewer = gallery.querySelector('[id^="GalleryViewer"]');
      var rail = gallery.querySelector('[id^="GalleryThumbnails"]');
      if (!viewer || !rail) return;
      var list = rail.querySelector('.thumbnail-list');
      var mq = window.matchMedia('(min-width: 990px)');
      var vtoOpen = false; // true while the Virtual Try-On overlay is showing

      // Cap the rail to the main-image height. Guarded so it NEVER publishes a 0
      // (or tiny) height: while the VTO camera is open the viewer's in-flow image
      // can momentarily report 0, and propagating that would collapse the rail.
      function syncHeight() {
        if (!mq.matches || vtoOpen) return;
        var h = viewer.offsetHeight;
        if (h && h > 80) document.documentElement.style.setProperty('--pdp-rail-h', h + 'px');
      }

      // Build the vertical slider arrows once (up/down). They page the list
      // instead of a scrollbar — the same slider UX the horizontal thumbnails had.
      var up, down;
      function buildArrows() {
        if (up || !list) return;
        up = document.createElement('button');
        up.type = 'button';
        up.className = 'pdp-rail-arrow pdp-rail-arrow--up';
        up.setAttribute('aria-label', 'Previous thumbnails');
        up.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 15 6-6 6 6"/></svg>';
        down = document.createElement('button');
        down.type = 'button';
        down.className = 'pdp-rail-arrow pdp-rail-arrow--down';
        down.setAttribute('aria-label', 'More thumbnails');
        down.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';
        rail.appendChild(up);
        rail.appendChild(down);
        up.addEventListener('click', function () { page(-1); });
        down.addEventListener('click', function () { page(1); });
      }

      // Pinned Try-On tile height (so the up arrow sits just below it). The tile
      // now lives in the rail (a sibling of the list), not inside the list.
      function vtoPad() {
        var vto = rail.querySelector('.pdp-vto-thumb');
        return vto ? vto.offsetHeight + 14 : 8;
      }
      function positionUpArrow() {
        if (up) up.style.top = vtoPad() + 'px';
      }
      function stepPx() {
        var item = list && list.querySelector('.thumbnail-list__item');
        var itemH = item ? item.getBoundingClientRect().height : 90;
        // page ~2 thumbnails, capped to the visible height
        return Math.max(60, Math.min(list.clientHeight, (itemH + 10) * 2));
      }
      function page(dir) {
        if (!list) return;
        list.scrollTop += dir * stepPx();
        setTimeout(updateArrows, 280);
      }
      function updateArrows() {
        if (!up || !list) return;
        positionUpArrow();
        var canScroll = mq.matches && list.scrollHeight > list.clientHeight + 2;
        if (!canScroll) { up.hidden = true; down.hidden = true; return; }
        up.hidden = list.scrollTop <= 1;
        down.hidden = (list.scrollTop + list.clientHeight) >= (list.scrollHeight - 1);
      }

      // Auto-scroll so a clicked thumbnail that is partially hidden / off-screen
      // (e.g. near the bottom) becomes fully visible without touching the arrows.
      function revealThumb(el) {
        if (!list || !el || !mq.matches) return;
        var listRect = list.getBoundingClientRect();
        var elRect = el.getBoundingClientRect();
        var relTop = (elRect.top - listRect.top) + list.scrollTop;
        var relBottom = relTop + el.offsetHeight;
        var pad = 34; // keep clear of the reserved arrow strips (list padding)
        if (relTop - pad < list.scrollTop) {
          list.scrollTop = Math.max(0, relTop - pad - 6);
        } else if (relBottom + pad > list.scrollTop + list.clientHeight) {
          list.scrollTop = relBottom - list.clientHeight + pad + 6;
        }
        setTimeout(updateArrows, 280);
      }

      function syncAll() { syncHeight(); positionUpArrow(); updateArrows(); }

      buildArrows();
      syncHeight();
      updateArrows();
      if (list) {
        list.addEventListener('scroll', updateArrows, { passive: true });
        // Reveal a clicked product thumbnail if it isn't fully in view.
        list.addEventListener('click', function (e) {
          var item = e.target.closest && e.target.closest('.thumbnail-list__item');
          if (item && !item.classList.contains('pdp-vto-thumb')) {
            setTimeout(function () { revealThumb(item); }, 0);
          }
        });
      }
      window.addEventListener('resize', syncAll);
      window.addEventListener('load', syncAll);
      if (typeof window.ResizeObserver !== 'undefined') {
        try { new ResizeObserver(syncAll).observe(viewer); } catch (e) { /* no-op */ }
        try { if (list) new ResizeObserver(updateArrows).observe(list); } catch (e) { /* no-op */ }
      }
      // Re-measure after images finish loading (height settles late).
      var imgs = viewer.querySelectorAll('img');
      for (var i = 0; i < imgs.length; i++) {
        if (!imgs[i].complete) imgs[i].addEventListener('load', syncAll, { once: true });
      }

      // --- Virtual Try-On: keep the gallery height stable ---------------------
      // The FitMix SDK takes over #fitmix-container on startVto and can drop the
      // viewer's in-flow image, which would collapse the viewer (and, via
      // --pdp-rail-h, the thumbnail rail) to 0. We watch the fitmix container's
      // display: when the try-on opens we PIN the viewer's current height (so it
      // can't collapse — the absolute camera fills it) and freeze the rail; when
      // it closes we release the pin and re-measure. No VTO logic is touched.
      var fitmix = gallery.querySelector('#fitmix-container');
      var lastActiveThumb = null;
      // Re-assert the previously selected product image after the try-on closes,
      // so the main stage is never left blank (the camera overlay can leave the
      // active slide un-rendered). We simply re-click the thumbnail that was
      // active when the try-on opened — Dawn then re-renders its image and marks
      // it current. No gallery/VTO logic is modified.
      function restoreActiveMedia() {
        try {
          var target = lastActiveThumb ||
            rail.querySelector('.thumbnail[aria-current="true"]') ||
            rail.querySelector('.thumbnail[aria-current]');
          if (!target && list) {
            target = list.querySelector('.thumbnail-list__item:not(.pdp-vto-thumb) .thumbnail');
          }
          if (target) target.click();
        } catch (e) { /* no-op */ }
      }
      if (fitmix && typeof window.MutationObserver !== 'undefined') {
        var applyVto = function () {
          var open = fitmix.style.display && fitmix.style.display !== 'none';
          if (open === vtoOpen) return;
          vtoOpen = open;
          if (open) {
            // remember what was showing so we can restore it on close
            lastActiveThumb = rail.querySelector('.thumbnail[aria-current="true"]') ||
                              rail.querySelector('.thumbnail[aria-current]') || lastActiveThumb;
            var h = viewer.offsetHeight;
            if (h && h > 80) {
              viewer.style.minHeight = h + 'px'; // lock the stage height
            }
          } else {
            viewer.style.minHeight = '';
            syncAll();
            restoreActiveMedia(); // never leave the main image blank
          }
        };
        try {
          new MutationObserver(applyVto).observe(fitmix, {
            attributes: true,
            attributeFilter: ['style']
          });
        } catch (e) { /* no-op */ }
      }
    } catch (e) { /* no-op */ }
  }
})();
